// IndexedDB wrapper for Songwriter's Toolbox
// Local-first storage - no cloud, no accounts, everything stays on device

const DB_NAME = 'songwriters-toolbox';
const DB_VERSION = 1;
const SONGS_STORE = 'songs';
const AUDIO_STORE = 'audio';

let dbInstance = null;

// Generate a unique ID
export function generateId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Initialize the database
function initDB() {
    return new Promise((resolve, reject) => {
        if (dbInstance) {
            resolve(dbInstance);
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            console.error('Failed to open IndexedDB');
            reject(request.error);
        };

        request.onsuccess = () => {
            dbInstance = request.result;
            resolve(dbInstance);
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;

            // Create songs store
            if (!db.objectStoreNames.contains(SONGS_STORE)) {
                const songsStore = db.createObjectStore(SONGS_STORE, { keyPath: 'id' });
                songsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            }

            // Create audio blobs store
            if (!db.objectStoreNames.contains(AUDIO_STORE)) {
                db.createObjectStore(AUDIO_STORE, { keyPath: 'id' });
            }
        };
    });
}

// Database operations
export const db = {
    // Get all songs, sorted by last modified
    async getAllSongs() {
        const database = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(SONGS_STORE, 'readonly');
            const store = transaction.objectStore(SONGS_STORE);
            const request = store.getAll();

            request.onsuccess = () => {
                const songs = request.result;
                // Sort by updatedAt descending
                songs.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
                resolve(songs);
            };

            request.onerror = () => reject(request.error);
        });
    },

    // Get a single song by ID
    async getSong(id) {
        const database = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(SONGS_STORE, 'readonly');
            const store = transaction.objectStore(SONGS_STORE);
            const request = store.get(id);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    },

    // Save a song (create or update)
    async saveSong(song) {
        const database = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(SONGS_STORE, 'readwrite');
            const store = transaction.objectStore(SONGS_STORE);
            const request = store.put(song);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    },

    // Delete a song
    async deleteSong(id) {
        const database = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(SONGS_STORE, 'readwrite');
            const store = transaction.objectStore(SONGS_STORE);
            const request = store.delete(id);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    },

    // Save audio blob
    async saveAudioBlob(id, blob) {
        const database = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(AUDIO_STORE, 'readwrite');
            const store = transaction.objectStore(AUDIO_STORE);
            const request = store.put({ id, blob, createdAt: new Date().toISOString() });

            request.onsuccess = () => resolve(id);
            request.onerror = () => reject(request.error);
        });
    },

    // Get audio blob
    async getAudioBlob(id) {
        const database = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(AUDIO_STORE, 'readonly');
            const store = transaction.objectStore(AUDIO_STORE);
            const request = store.get(id);

            request.onsuccess = () => {
                if (request.result) {
                    resolve(request.result.blob);
                } else {
                    resolve(null);
                }
            };
            request.onerror = () => reject(request.error);
        });
    },

    // Delete audio blob
    async deleteAudioBlob(id) {
        const database = await initDB();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(AUDIO_STORE, 'readwrite');
            const store = transaction.objectStore(AUDIO_STORE);
            const request = store.delete(id);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
};